import React from 'react';
import { createPortal } from 'react-dom';
import Card from '../../../../../shared/components/Card';
import { UserWithRole, Role } from '../../../../../shared/services/userService';
import usePageTranslation from '../../../../../shared/hooks/usePageTranslation';

interface RoleChangeModalProps {
  user: UserWithRole;
  roles: Role[];
  newRoleId: number;
  reason: string;
  updating: boolean;
  onRoleIdChange: (roleId: number) => void;
  onReasonChange: (reason: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

const RoleChangeModal: React.FC<RoleChangeModalProps> = ({
  user,
  roles,
  newRoleId,
  reason,
  updating,
  onRoleIdChange,
  onReasonChange,
  onConfirm,
  onCancel
}) => {
  const { t } = usePageTranslation('panel', 'UserRolesManagement');

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
      <Card className="max-w-md w-full p-6">
        <h3 className="text-2xl font-bold text-white mb-4">
          {t('modal.changeRole.title')} - {user.username}
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t('modal.changeRole.newRole')}
            </label>
            <select
              value={newRoleId}
              onChange={(e) => onRoleIdChange(Number(e.target.value))}
              className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.role_name} - {role.description}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t('modal.changeRole.reason')} ({t('modal.changeRole.optional')})
            </label>
            <textarea
              value={reason}
              onChange={(e) => onReasonChange(e.target.value)}
              placeholder={t('modal.changeRole.reasonPlaceholder')}
              rows={3}
              className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onConfirm}
              disabled={updating || newRoleId === user.roles_id}
              className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updating ? t('modal.changeRole.updating') : t('modal.changeRole.confirm')}
            </button>
            <button
              onClick={onCancel}
              className="flex-1 px-4 py-3 bg-gray-700 text-white rounded-lg font-semibold hover:bg-gray-600 transition-colors"
            >
              {t('modal.changeRole.cancel')}
            </button>
          </div>
        </div>
      </Card>
    </div>,
    document.body
  );
};

export default RoleChangeModal;

